/** Iter 109 — impersonation audit history panel (Admin → Tools). */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { ShieldAlert, RefreshCw } from "lucide-react";

function fmt(iso) {
  return iso ? String(iso).slice(0, 16).replace("T", " ") : "—";
}

function duration(rec) {
  if (!rec.revoked_at) return "active";
  const ms = new Date(rec.revoked_at) - new Date(rec.created_at);
  const m = Math.max(0, Math.round(ms / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

export default function AdminImpersonationLogPanel() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/impersonate/log?limit=100").then((r) => setRows(r.data.items || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid="impersonation-log-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-bold flex items-center gap-1.5"><ShieldAlert className="h-4 w-4 text-rose-600" /> Impersonation history</div>
          <div className="text-xs text-neutral-500">Every "Open Vendor/Client Dashboard" session — who, whom, when, how long.</div>
        </div>
        <button type="button" onClick={load} className="text-neutral-400 hover:text-neutral-700" data-testid="impersonation-log-refresh" aria-label="Refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      {rows === null ? (
        <div className="text-sm text-neutral-400 py-4 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-neutral-400 py-4 text-center" data-testid="impersonation-log-empty">No impersonation sessions yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-[10px] uppercase tracking-wider text-neutral-500 bg-neutral-50/60">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Started</th>
                <th className="text-left px-3 py-2 font-bold">Admin</th>
                <th className="text-left px-3 py-2 font-bold">Viewed as</th>
                <th className="text-left px-3 py-2 font-bold">Role</th>
                <th className="text-left px-3 py-2 font-bold">Ended</th>
                <th className="text-right px-3 py-2 font-bold">Duration</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`impersonation-log-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="px-3 py-2 font-semibold">{r.actor_admin_name}</td>
                  <td className="px-3 py-2">{r.target_name}</td>
                  <td className="px-3 py-2 capitalize">{r.target_role}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(r.revoked_at)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${r.revoked_at ? "" : "text-rose-600"}`}>{duration(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
